import { useState, useRef, useCallback } from "react";
import { IMAGES } from "@/data/images";

const LEGEND = [
  { label: "Normaal", color: "#2B9FD7" },
  { label: "Verhoogd", color: "#62C3F0" },
  { label: "Vooralarm", color: "#FF8A00" },
  { label: "Alarm", color: "#E5483F" },
];

export const ThermalSlider = () => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) updateFromClientX(e.clientX);
  };
  const onPointerUp = () => {
    dragging.current = false;
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 4));
  };

  const alarmVisible = pos < 52;

  return (
    <div className="w-full">
      <div
        ref={ref}
        className="relative aspect-[3/2] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-2xl border border-white/10"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        data-testid="thermal-slider"
      >
        <img
          src={IMAGES.bunkerOptical}
          alt="Normaal optisch camerabeeld van een afvalbunker"
          className="absolute inset-0 h-full w-full object-cover"
          draggable="false"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
        >
          <img
            src={IMAGES.bunkerThermal}
            alt="Thermisch camerabeeld van dezelfde afvalbunker met zichtbare hotspot"
            className="absolute inset-0 h-full w-full object-cover"
            draggable="false"
            loading="lazy"
          />
          <div className="absolute left-[50%] top-[62%]" aria-hidden="true">
            <span className="hotspot-ring absolute -left-6 -top-6 h-12 w-12 rounded-full border-2 border-[#FF8A00]" />
            <span className="hotspot-core absolute -left-1.5 -top-1.5 h-3 w-3 rounded-full bg-[#FF8A00]" />
            <span className="absolute -top-14 left-3 whitespace-nowrap rounded-md border border-[#FF8A00]/50 bg-[#090D10]/85 px-2.5 py-1 font-heading text-xs font-bold text-[#FF8A00] backdrop-blur-sm">
              78,4 °C
            </span>
          </div>
          <span className="absolute bottom-3 right-3 rounded bg-[#090D10]/70 px-2 py-1 text-[10px] text-[#CBD3D9]/80 backdrop-blur-sm">
            Visualisatie ter illustratie.
          </span>
        </div>

        <div className="scan-line" aria-hidden="true" />

        <div
          className="absolute inset-y-0 z-10 w-0.5 bg-[#62C3F0]"
          style={{ left: `${pos}%` }}
          aria-hidden="true"
        />
        <button
          type="button"
          role="slider"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pos)}
          aria-label="Vergelijk optisch en thermisch beeld"
          onKeyDown={onKeyDown}
          className="absolute z-20 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#62C3F0] bg-[#090D10]/90 text-[#62C3F0] shadow-[0_0_20px_rgba(98,195,240,0.45)] backdrop-blur-sm"
          style={{ left: `${pos}%`, top: "50%" }}
          data-testid="thermal-slider-handle"
        >
          <svg width="18" height="12" viewBox="0 0 18 12" fill="none" aria-hidden="true">
            <path d="M5 1 1 6l4 5M13 1l4 5-4 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <span className="absolute left-3 top-3 rounded-full bg-[#090D10]/70 px-3 py-1 font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-[#CBD3D9] backdrop-blur-sm">
          Optisch
        </span>
        <span className="absolute right-3 top-3 rounded-full bg-[#090D10]/70 px-3 py-1 font-heading text-[10px] font-semibold uppercase tracking-[0.2em] text-[#FF8A00] backdrop-blur-sm">
          Thermisch
        </span>

        {alarmVisible && (
          <div
            className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[#FF8A00]/60 bg-[#090D10]/90 px-4 py-2 backdrop-blur-md"
            data-testid="thermal-slider-alert"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#FF8A00]" />
            <span className="font-heading text-xs font-semibold text-[#FF8A00]">
              Vooralarm · Zone 3 · grenswaarde overschreden (fictief)
            </span>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4" aria-label="Temperatuurlegenda">
          {LEGEND.map((item) => (
            <span key={item.label} className="flex items-center gap-2 text-xs text-[#CBD3D9]">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
        <div className="h-2 w-40 rounded-full bg-gradient-to-r from-[#2B9FD7] via-[#62C3F0] via-40% to-[#E5483F]" aria-hidden="true" />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-[#CBD3D9]/60">
        Deze demonstratie is uitsluitend een visuele uitleg. Werkelijke meetwaarden en alarmgrenzen
        worden per locatie en toepassing ingesteld.
      </p>
    </div>
  );
};
